import {Controller} from '@hotwired/stimulus';

const DATE_PATTERN = /^(\d{1,2})[/\s.-]+(\d{1,2})[/\s.-]+(\d{4}|\d{2})$/;

// la barre d'actions des formulaires d'édition reste collée en bas de la fenêtre
const BOTTOM_BAR_HEIGHT = 96;

const ICONS = {
  previousMonth: '<path d="m15 18-6-6 6-6"/>',
  nextMonth: '<path d="m9 18 6-6-6-6"/>',
};

/**
 * Calendrier d'un champ date (date-input.md › picker), sur le modèle « date picker dialog » de l'APG du W3C.
 *
 * Posé par `DateTimePickerType` sur le groupe champ + bouton : le champ (`input`) reste saisissable et seul
 * posté, en jj/mm/aaaa ; le bouton (`toggle`) ouvre un panneau `role="dialog"` construit au premier usage.
 * Flèches pour les jours, Début et Fin pour la semaine, Page préc. et Page suiv. pour les mois (avec Maj pour
 * les années), Entrée ou Espace pour choisir, Échap pour fermer : le focus revient au champ. Une saisie lisible
 * est remise au format au `change` (« 14 10 26 » devient « 14/10/2026 »).
 *
 * `minInput` et `maxInput` : id d'un autre champ date qui borne celui-ci, pour qu'une fin de période ne précède
 * jamais son début. Les jours hors bornes sont annoncés indisponibles et ne se choisissent pas.
 */
export default class extends Controller {
  static targets = ['input', 'toggle'];

  static values = {locale: String, labels: Object, minInput: String, maxInput: String};

  connect() {
    this.closeOnOutsideClick = this.closeOnOutsideClick.bind(this);
  }

  disconnect() {
    this.close(false);
  }

  toggle() {
    if (this.dialog && !this.dialog.hidden) {
      this.close(true);
    } else {
      this.open();
    }
  }

  normalize() {
    const date = parseDate(this.inputTarget.value);

    if (date) {
      this.inputTarget.value = formatDate(date);
    }
  }

  open() {
    if (!this.dialog) {
      this.build();
    }

    this.selected = parseDate(this.inputTarget.value);
    this.min = this.boundFrom(this.minInputValue);
    this.max = this.boundFrom(this.maxInputValue);
    this.focused = this.clamp(this.selected ?? today());
    this.render();

    this.dialog.hidden = false;
    this.toggleTarget.setAttribute('aria-expanded', 'true');
    this.place();
    this.focusedCell.focus();

    document.addEventListener('pointerdown', this.closeOnOutsideClick);
  }

  close(returnFocus) {
    if (!this.dialog || this.dialog.hidden) {
      return;
    }

    this.dialog.hidden = true;
    this.toggleTarget.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.closeOnOutsideClick);

    if (returnFocus) {
      this.inputTarget.focus();
    }
  }

  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close(false);
    }
  }

  previousMonth() {
    this.moveTo(addMonths(this.focused, -1), false);
  }

  nextMonth() {
    this.moveTo(addMonths(this.focused, 1), false);
  }

  choose(event) {
    const cell = event.target.closest('td[data-date]');

    if (cell) {
      this.select(fromIso(cell.dataset.date));
    }
  }

  navigate(event) {
    const day = (this.focused.getDay() + 6) % 7;
    const moves = {
      ArrowLeft: () => addDays(this.focused, -1),
      ArrowRight: () => addDays(this.focused, 1),
      ArrowUp: () => addDays(this.focused, -7),
      ArrowDown: () => addDays(this.focused, 7),
      Home: () => addDays(this.focused, -day),
      End: () => addDays(this.focused, 6 - day),
      PageUp: () => addMonths(this.focused, event.shiftKey ? -12 : -1),
      PageDown: () => addMonths(this.focused, event.shiftKey ? 12 : 1),
    };

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.select(this.focused);
    } else if (moves[event.key]) {
      event.preventDefault();
      this.moveTo(moves[event.key](), true);
    }
  }

  trapFocus(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);

      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const stops = [...this.navButtons, this.focusedCell];
    const index = stops.indexOf(document.activeElement);
    const next = event.shiftKey ? index - 1 : index + 1;

    event.preventDefault();
    stops[(next + stops.length) % stops.length].focus();
  }

  select(date) {
    if (this.isDisabled(date)) {
      return;
    }

    this.inputTarget.value = formatDate(date);
    this.inputTarget.dispatchEvent(new Event('input', {bubbles: true}));
    this.inputTarget.dispatchEvent(new Event('change', {bubbles: true}));
    this.close(true);
  }

  moveTo(date, focusDay) {
    this.focused = date;
    this.render();

    if (focusDay) {
      this.focusedCell.focus();
    }
  }

  build() {
    const id = `${this.inputTarget.id}_calendar`;

    this.dialog = element('div', {
      'class': 'date-picker-dialog',
      'role': 'dialog',
      'aria-modal': 'true',
      'aria-label': this.labelsValue.dialog,
      'data-action': 'keydown->date-picker#trapFocus',
      'hidden': '',
    });

    const header = element('div', {class: 'date-picker-header'});
    this.navButtons = ['previousMonth', 'nextMonth'].map((action) => {
      const button = element('button', {
        'type': 'button',
        'class': 'date-picker-nav',
        'aria-label': this.labelsValue[action],
        'data-action': `date-picker#${action}`,
      });
      button.innerHTML = icon(ICONS[action]);

      return button;
    });
    this.heading = element('h2', {'class': 'date-picker-month', 'id': `${id}_month`, 'aria-live': 'polite'});
    header.append(this.navButtons[0], this.heading, this.navButtons[1]);

    const grid = element('table', {'class': 'date-picker-grid', 'role': 'grid', 'aria-labelledby': `${id}_month`});
    const head = element('tr');
    const narrow = new Intl.DateTimeFormat(this.localeValue, {weekday: 'narrow'});
    const long = new Intl.DateTimeFormat(this.localeValue, {weekday: 'long'});
    const monday = new Date(2024, 0, 1);

    for (let i = 0; i < 7; i++) {
      const day = addDays(monday, i);
      const th = element('th', {scope: 'col', abbr: long.format(day)});
      th.textContent = narrow.format(day);
      head.append(th);
    }

    this.body = element('tbody', {'data-action': 'keydown->date-picker#navigate click->date-picker#choose'});
    grid.append(element('thead'), this.body);
    grid.tHead.append(head);

    this.dialog.append(header, grid);
    this.element.append(this.dialog);

    this.monthFormat = new Intl.DateTimeFormat(this.localeValue, {month: 'long', year: 'numeric'});
    this.dayFormat = new Intl.DateTimeFormat(this.localeValue, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  render() {
    const first = new Date(this.focused.getFullYear(), this.focused.getMonth(), 1);
    const start = addDays(first, -((first.getDay() + 6) % 7));
    const current = today();

    this.heading.textContent = this.monthFormat.format(first);
    this.body.replaceChildren();

    // six semaines, pour que le panneau garde sa hauteur d'un mois à l'autre
    for (let week = 0; week < 6; week++) {
      const row = element('tr');

      for (let i = 0; i < 7; i++) {
        const date = addDays(start, week * 7 + i);

        if (date.getMonth() !== first.getMonth()) {
          row.append(element('td'));
          continue;
        }

        const cell = element('td', {
          'data-date': toIso(date),
          'tabindex': sameDay(date, this.focused) ? '0' : '-1',
          'aria-label': this.dayFormat.format(date),
        });
        cell.textContent = String(date.getDate());

        if (this.selected && sameDay(date, this.selected)) {
          cell.setAttribute('aria-selected', 'true');
        }

        if (sameDay(date, current)) {
          cell.setAttribute('aria-current', 'date');
        }

        if (this.isDisabled(date)) {
          cell.setAttribute('aria-disabled', 'true');
        }

        row.append(cell);
      }

      this.body.append(row);
    }
  }

  place() {
    this.dialog.classList.remove('date-picker-dialog-up');

    const panel = this.dialog.getBoundingClientRect();
    const field = this.element.getBoundingClientRect();

    if (panel.bottom > window.innerHeight - BOTTOM_BAR_HEIGHT && field.top > panel.height) {
      this.dialog.classList.add('date-picker-dialog-up');
    }
  }

  boundFrom(id) {
    const input = id ? document.getElementById(id) : null;

    return input ? parseDate(input.value) : null;
  }

  clamp(date) {
    if (this.min && date < this.min) {
      return this.min;
    }

    if (this.max && date > this.max) {
      return this.max;
    }

    return date;
  }

  isDisabled(date) {
    return Boolean((this.min && date < this.min) || (this.max && date > this.max));
  }

  get focusedCell() {
    return this.body.querySelector(`td[data-date="${toIso(this.focused)}"]`);
  }
}

/**
 * Lit « 14/10/2026 », « 14 10 2026 », « 14-10-2026 » ou « 14/10/26 » ; null pour une saisie vide, illisible ou
 * une date qui n'existe pas.
 */
function parseDate(value) {
  const parts = DATE_PATTERN.exec(value.trim());

  if (!parts) {
    return null;
  }

  const day = Number(parts[1]);
  const month = Number(parts[2]) - 1;
  const year = parts[3].length === 2 ? 2000 + Number(parts[3]) : Number(parts[3]);
  const date = new Date(year, month, day);

  return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day ? date : null;
}

function formatDate(date) {
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

function toIso(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function fromIso(value) {
  const [year, month, day] = value.split('-').map(Number);

  return new Date(year, month - 1, day);
}

function pad(number) {
  return String(number).padStart(2, '0');
}

function today() {
  const now = new Date();

  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function addDays(date, days) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/**
 * Même jour du mois cible, ramené à son dernier jour s'il est plus court (31 janvier + 1 mois = 28 février).
 */
function addMonths(date, months) {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();

  return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), lastDay));
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function element(tag, attributes = {}) {
  const node = document.createElement(tag);

  Object.entries(attributes).forEach(([name, value]) => node.setAttribute(name, value));

  return node;
}

function icon(paths) {
  return '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" ' +
    `focusable="false">${paths}</svg>`;
}
